import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import {
    Pencil,
    AtSign,
    Phone,
    MapPin,
    Globe,
    MessageSquare,
    Plus,
    Trash2
} from 'lucide-react';
import { API } from '../../config';

const AdminFooter = () => {
    const [settings, setSettings] = useState({
        tagline: '',
        description: '',
        email: '',
        phone: '',
        location: '',
        copyright: '',
        facebook: '',
        instagram: '',
        linkedin: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);


    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API}/footer`);
            setSettings(response.data);
        } catch (error) {
            console.error('Failed to fetch footer settings:', error);
            toast.error('Failed to load footer settings');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/footer`, settings, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Footer updated successfully!');
            fetchSettings();
        } catch (error) {
            toast.error('Failed to update footer');
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-[2.5rem] shadow-sm overflow-hidden border border-agro-green-light/5"
        >
            <div className="p-8 lg:p-12">
                <div className="flex justify-between items-center mb-12">
                    <div>
                        <h2 className="text-2xl font-serif text-agro-black">Footer Architecture</h2>
                        <p className="text-agro-green/40 text-xs font-bold uppercase tracking-widest mt-1">Manage global brand identity and contact points</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-10 max-w-5xl">
                    {/* Brand Info */}
                    <div className="grid grid-cols-1 gap-8 p-8 bg-agro-green-light/5 rounded-[2rem] border border-agro-green-light/10">
                        <h3 className="text-xs font-bold text-agro-green-dark uppercase tracking-[0.2em] flex items-center">
                            <MessageSquare className="h-4 w-4 mr-3 text-agro-green" />
                            Brand Identity
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <label className="block text-[10px] font-bold text-agro-green/60 mb-2 uppercase tracking-widest">Tagline / Mission Statement</label>
                                <input
                                    type="text"
                                    value={settings.tagline}
                                    onChange={e => setSettings({ ...settings, tagline: e.target.value })}
                                    className="w-full px-5 py-3 border border-agro-green-light/20 rounded-xl focus:ring-2 focus:ring-agro-green/10 focus:border-agro-green bg-white text-sm outline-none transition-all"
                                    placeholder="e.g., Pure Harvest • Sustainable Future • Ethical Farming"
                                />
                            </div>
                            <div className="md:col-span-1">
                                <label className="block text-[10px] font-bold text-agro-green/60 mb-2 uppercase tracking-widest">Short Description</label>
                                <textarea
                                    value={settings.description}
                                    onChange={e => setSettings({ ...settings, description: e.target.value })}
                                    className="w-full px-5 py-3 border border-agro-green-light/20 rounded-xl focus:ring-2 focus:ring-agro-green/10 focus:border-agro-green bg-white text-sm outline-none transition-all"
                                    placeholder="Detail what makes SR Agro Farms unique..."
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Contact Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8 bg-agro-green-light/5 rounded-[2rem] border border-agro-green-light/10">
                        <h3 className="text-xs font-bold text-agro-green-dark uppercase tracking-[0.2em] flex items-center col-span-full">
                            <Phone className="h-4 w-4 mr-3 text-agro-green" />
                            Connectivity
                        </h3>
                        <div>
                            <label className="block text-[10px] font-bold text-agro-green/60 mb-2 uppercase tracking-widest">Support Email</label>
                            <div className="relative">
                                <AtSign className="h-4 w-4 text-agro-green/30 absolute left-4 top-1/2 transform -translate-y-1/2" />
                                <input
                                    type="email"
                                    value={settings.email}
                                    onChange={e => setSettings({ ...settings, email: e.target.value })}
                                    className="w-full pl-12 pr-5 py-3 border border-agro-green-light/20 rounded-xl focus:ring-2 focus:ring-agro-green/10 focus:border-agro-green bg-white text-sm outline-none transition-all"
                                    placeholder="hello@sragrofarms.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-agro-green/60 mb-2 uppercase tracking-widest">Primary Phone</label>
                            <div className="relative">
                                <Phone className="h-4 w-4 text-agro-green/30 absolute left-4 top-1/2 transform -translate-y-1/2" />
                                <input
                                    type="text"
                                    value={settings.phone}
                                    onChange={e => setSettings({ ...settings, phone: e.target.value })}
                                    className="w-full pl-12 pr-5 py-3 border border-agro-green-light/20 rounded-xl focus:ring-2 focus:ring-agro-green/10 focus:border-agro-green bg-white text-sm outline-none transition-all"
                                    placeholder="+91 88888 77777"
                                />
                            </div>
                        </div>
                        <div className="md:col-span-full">
                            <label className="block text-[10px] font-bold text-agro-green/60 mb-2 uppercase tracking-widest">Headquarters Address</label>
                            <div className="relative">
                                <MapPin className="h-4 w-4 text-agro-green/30 absolute left-4 top-4" />
                                <textarea
                                    value={settings.location}
                                    onChange={e => setSettings({ ...settings, location: e.target.value })}
                                    className="w-full pl-12 pr-5 py-3 border border-agro-green-light/20 rounded-xl focus:ring-2 focus:ring-agro-green/10 focus:border-agro-green bg-white text-sm outline-none transition-all"
                                    placeholder="Complete farm or office address..."
                                    rows={2}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Links & Social */}
                    <div className="grid grid-cols-1 gap-8 p-8 bg-agro-green-light/5 rounded-[2rem] border border-agro-green-light/10">
                        <h3 className="text-xs font-bold text-agro-green-dark uppercase tracking-[0.2em] flex items-center">
                            <Globe className="h-4 w-4 mr-3 text-agro-green" />
                            Navigation & Presence
                        </h3>
                        <div className="space-y-4">
                            <label className="block text-[10px] font-bold text-agro-green/60 mb-2 uppercase tracking-widest">Quick Navigation Links</label>
                            {settings.links?.map((link, idx) => (
                                <div key={idx} className="flex flex-col md:flex-row gap-3 p-4 bg-white rounded-2xl border border-agro-green-light/10 shadow-sm">
                                    <input
                                        type="text"
                                        value={link.label}
                                        onChange={e => {
                                            const newLinks = [...settings.links];
                                            newLinks[idx].label = e.target.value;
                                            setSettings({ ...settings, links: newLinks });
                                        }}
                                        className="flex-1 px-4 py-2 border border-agro-green-light/10 rounded-lg text-sm bg-agro-green-light/5 outline-none"
                                        placeholder="Display Label"
                                    />
                                    <select
                                        value={
                                            ['/', '/products', '/cart', '/profile'].includes(link.url)
                                                ? link.url : (link.url ? 'custom' : '')
                                        }
                                        onChange={e => {
                                            const val = e.target.value;
                                            const newLinks = [...settings.links];
                                            if (val === 'custom') {
                                                newLinks[idx].url = '';
                                            } else {
                                                newLinks[idx].url = val;
                                                if (!newLinks[idx].label || newLinks[idx].label === 'Select Page/Category') {
                                                    const selectedOption = e.target.options[e.target.selectedIndex];
                                                    newLinks[idx].label = selectedOption.text;
                                                }
                                            }
                                            setSettings({ ...settings, links: newLinks });
                                        }}
                                        className="flex-1 px-4 py-2 border border-agro-green-light/10 rounded-lg text-sm bg-agro-green-light/5 outline-none"
                                    >
                                        <option value="">Link to...</option>
                                        <optgroup label="Main Pages">
                                            <option value="/">Home</option>
                                            <option value="/profile">Account</option>
                                        </optgroup>
                                        <option value="custom">-- Custom URL --</option>
                                    </select>

                                    {(!['/', '/products', '/cart', '/profile'].includes(link.url)) && (
                                        <input
                                            type="text"
                                            value={link.url}
                                            onChange={e => {
                                                const newLinks = [...settings.links];
                                                newLinks[idx].url = e.target.value;
                                                setSettings({ ...settings, links: newLinks });
                                            }}
                                            className="flex-1 px-4 py-2 border border-agro-green-light/10 rounded-lg text-sm bg-agro-green-light/5 outline-none"
                                            placeholder="Custom path (e.g. /about)"
                                        />
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newLinks = settings.links.filter((_, i) => i !== idx);
                                            setSettings({ ...settings, links: newLinks });
                                        }}
                                        className="p-2 text-red-400 hover:text-red-600 transition-colors"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => setSettings({ ...settings, links: [...(settings.links || []), { label: '', url: '' }] })}
                                className="inline-flex items-center gap-2 text-xs font-bold text-agro-green hover:text-agro-green-dark transition-colors uppercase tracking-widest px-4 py-2 border border-agro-green/20 rounded-xl"
                            >
                                <Plus className="h-3 w-3" /> Add Navigation Point
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-6">
                            <div>
                                <label className="block text-[10px] font-bold text-agro-green/60 mb-2 uppercase tracking-widest">Instagram</label>
                                <input
                                    type="url"
                                    value={settings.instagram || ''}
                                    onChange={e => setSettings({ ...settings, instagram: e.target.value })}
                                    className="w-full px-5 py-3 border border-agro-green-light/20 rounded-xl bg-white text-sm outline-none"
                                    placeholder="https://..."
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-agro-green/60 mb-2 uppercase tracking-widest">Facebook</label>
                                <input
                                    type="url"
                                    value={settings.facebook || ''}
                                    onChange={e => setSettings({ ...settings, facebook: e.target.value })}
                                    className="w-full px-5 py-3 border border-agro-green-light/20 rounded-xl bg-white text-sm outline-none"
                                    placeholder="https://..."
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-agro-green/60 mb-2 uppercase tracking-widest">LinkedIn</label>
                                <input
                                    type="url"
                                    value={settings.linkedin || ''}
                                    onChange={e => setSettings({ ...settings, linkedin: e.target.value })}
                                    className="w-full px-5 py-3 border border-agro-green-light/20 rounded-xl bg-white text-sm outline-none"
                                    placeholder="https://..."
                                />
                            </div>
                            <div className="md:col-span-full">
                                <label className="block text-[10px] font-bold text-agro-green/60 mb-2 uppercase tracking-widest">Copyright Signature</label>
                                <input
                                    type="text"
                                    value={settings.copyright}
                                    onChange={e => setSettings({ ...settings, copyright: e.target.value })}
                                    className="w-full px-5 py-3 border border-agro-green-light/20 rounded-xl bg-white text-sm outline-none"
                                    placeholder="© 2025 SR Agro Farms. All rights reserved."
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-agro-green-dark text-white px-10 py-4 rounded-2xl hover:bg-agro-black transition-all shadow-xl shadow-agro-green-dark/20 text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-50 flex items-center gap-3"
                        >
                            {loading ? (
                                <div className="animate-spin h-4 w-4 border-2 border-white/20 border-t-white rounded-full" />
                            ) : (
                                <Pencil className="h-4 w-4" />
                            )}
                            Save Architectural Settings
                        </button>
                    </div>
                </form>
            </div>
        </motion.div>
    );
};

export default AdminFooter;
